import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  addMonths,
  addYears,
  endOfMonth,
  format,
  isValid,
  parseISO,
  startOfMonth,
  subMonths,
  subYears,
} from "date-fns";
import { CalendarIcon, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CaptionProps } from "react-day-picker";
import { useNavigation } from "react-day-picker";

export type DatePartsPickerProps = {
  value: string;
  onChange: (iso: string) => void;
  fromYear?: number;
  toYear?: number;
  maxDate?: Date;
  minDate?: Date;
  isDateDisabled?: (date: Date) => boolean;
  displayFormat?: string;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  className?: string;
  /** Popover horizontal alignment relative to the trigger. */
  align?: "start" | "center" | "end";
};

type PickerView = "days" | "months" | "years";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const YEAR_PAGE_SIZE = 12;

/** Fixed body height so the popover does not resize (and jump) when switching day/month/year views. */
const PICKER_BODY_HEIGHT_CLASS = "h-[18rem]";
/** Total popover height ≈ body + footer — used to pick top/bottom once on open. */
const PICKER_ESTIMATED_HEIGHT_PX = 380;

function parseIso(iso: string): Date | undefined {
  if (!iso.trim()) return undefined;
  const d = parseISO(iso.slice(0, 10));
  return isValid(d) ? d : undefined;
}

function displayValue(iso: string, dateFormat: string): string {
  const d = parseIso(iso);
  if (!d) return "";
  return format(d, dateFormat);
}

function isDateInRange(date: Date, min: Date, max: Date, maxDate?: Date): boolean {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const minDay = new Date(min.getFullYear(), min.getMonth(), min.getDate());
  const maxDay = new Date(max.getFullYear(), max.getMonth(), max.getDate());
  if (day < minDay || day > maxDay) return false;
  if (maxDate) {
    const cap = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    if (day > cap) return false;
  }
  return true;
}

function yearPageStart(year: number): number {
  return year - (year % YEAR_PAGE_SIZE);
}

function NavIconButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors",
        "hover:bg-muted hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        "disabled:pointer-events-none disabled:opacity-35",
      )}
    >
      {children}
    </button>
  );
}

function PickerShell({ children }: { children: ReactNode }) {
  return <div className="flex h-full flex-col p-3 pt-2">{children}</div>;
}

function GridCellButton({
  label,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-10 w-full rounded-md text-sm font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected
          ? "bg-primary text-primary-foreground hover:bg-primary/90"
          : "text-foreground hover:bg-muted",
        disabled && "pointer-events-none opacity-30",
      )}
    >
      {label}
    </button>
  );
}

function DayViewCaption({
  displayMonth,
  min,
  max,
  onOpenMonths,
  onOpenYears,
}: CaptionProps & {
  min: Date;
  max: Date;
  onOpenMonths: () => void;
  onOpenYears: () => void;
}) {
  const { goToMonth } = useNavigation();

  const prevMonth = subMonths(displayMonth, 1);
  const nextMonth = addMonths(displayMonth, 1);
  const prevYear = subYears(displayMonth, 1);
  const nextYear = addYears(displayMonth, 1);

  const minMonth = startOfMonth(min);
  const maxMonth = startOfMonth(max);

  return (
    <div className="flex items-center justify-between gap-1 border-b border-border px-0 pb-2 mb-2">
      <div className="flex items-center shrink-0">
        <NavIconButton
          label="Previous year"
          disabled={startOfMonth(prevYear) < minMonth}
          onClick={() => goToMonth(prevYear)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </NavIconButton>
        <NavIconButton
          label="Previous month"
          disabled={startOfMonth(prevMonth) < minMonth}
          onClick={() => goToMonth(prevMonth)}
        >
          <ChevronLeft className="h-4 w-4" />
        </NavIconButton>
      </div>

      <div className="flex items-center justify-center gap-1 min-w-0 text-sm font-semibold">
        <button
          type="button"
          onClick={onOpenMonths}
          className="truncate rounded px-1 py-0.5 text-foreground hover:bg-muted hover:text-primary transition-colors"
        >
          {format(displayMonth, "MMMM")}
        </button>
        <button
          type="button"
          onClick={onOpenYears}
          className="truncate rounded px-1 py-0.5 text-foreground hover:bg-muted hover:text-primary transition-colors tabular-nums"
        >
          {format(displayMonth, "yyyy")}
        </button>
      </div>

      <div className="flex items-center shrink-0">
        <NavIconButton
          label="Next month"
          disabled={startOfMonth(nextMonth) > maxMonth}
          onClick={() => goToMonth(nextMonth)}
        >
          <ChevronRight className="h-4 w-4" />
        </NavIconButton>
        <NavIconButton
          label="Next year"
          disabled={startOfMonth(nextYear) > maxMonth}
          onClick={() => goToMonth(nextYear)}
        >
          <ChevronsRight className="h-4 w-4" />
        </NavIconButton>
      </div>
    </div>
  );
}

function MonthPickerPanel({
  year,
  selected,
  min,
  max,
  onPrevYear,
  onNextYear,
  onOpenYears,
  onSelectMonth,
  canPrevYear,
  canNextYear,
}: {
  year: number;
  selected?: Date;
  min: Date;
  max: Date;
  onPrevYear: () => void;
  onNextYear: () => void;
  onOpenYears: () => void;
  onSelectMonth: (monthIndex: number) => void;
  canPrevYear: boolean;
  canNextYear: boolean;
}) {
  const selectedMonth = selected?.getMonth();
  const selectedYear = selected?.getFullYear();

  return (
    <PickerShell>
      <div className="flex items-center justify-between gap-2 border-b border-border pb-2 mb-3">
        <NavIconButton label="Previous year" disabled={!canPrevYear} onClick={onPrevYear}>
          <ChevronsLeft className="h-4 w-4" />
        </NavIconButton>
        <button
          type="button"
          onClick={onOpenYears}
          className="text-sm font-semibold text-foreground tabular-nums rounded px-2 py-0.5 hover:bg-muted hover:text-primary transition-colors"
        >
          {year}
        </button>
        <NavIconButton label="Next year" disabled={!canNextYear} onClick={onNextYear}>
          <ChevronsRight className="h-4 w-4" />
        </NavIconButton>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {MONTH_LABELS.map((label, monthIndex) => {
          const monthStart = new Date(year, monthIndex, 1);
          const monthEnd = endOfMonth(monthStart);
          const disabled = monthEnd < min || monthStart > max;
          const isSelected = selectedYear === year && selectedMonth === monthIndex;
          return (
            <GridCellButton
              key={label}
              label={label}
              selected={isSelected}
              disabled={disabled}
              onClick={() => onSelectMonth(monthIndex)}
            />
          );
        })}
      </div>
    </PickerShell>
  );
}

function YearPickerPanel({
  pageStart,
  focusYear,
  selected,
  minYear,
  maxYear,
  onPrevPage,
  onNextPage,
  onSelectYear,
  canPrevPage,
  canNextPage,
}: {
  pageStart: number;
  focusYear: number;
  selected?: Date;
  minYear: number;
  maxYear: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onSelectYear: (year: number) => void;
  canPrevPage: boolean;
  canNextPage: boolean;
}) {
  const years = Array.from({ length: YEAR_PAGE_SIZE }, (_, i) => pageStart + i);
  const selectedYear = selected?.getFullYear();

  return (
    <PickerShell>
      <div className="flex items-center justify-between gap-2 border-b border-border pb-2 mb-3">
        <NavIconButton label="Previous years" disabled={!canPrevPage} onClick={onPrevPage}>
          <ChevronsLeft className="h-4 w-4" />
        </NavIconButton>
        <span className="text-sm font-semibold text-foreground tabular-nums">{focusYear}</span>
        <NavIconButton label="Next years" disabled={!canNextPage} onClick={onNextPage}>
          <ChevronsRight className="h-4 w-4" />
        </NavIconButton>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {years.map((year) => {
          const disabled = year < minYear || year > maxYear;
          return (
            <GridCellButton
              key={year}
              label={String(year)}
              selected={selectedYear === year}
              disabled={disabled}
              onClick={() => onSelectYear(year)}
            />
          );
        })}
      </div>
    </PickerShell>
  );
}

export function DatePartsPicker({
  value,
  onChange,
  fromYear = 1940,
  toYear = new Date().getFullYear(),
  maxDate,
  minDate,
  isDateDisabled,
  displayFormat = "M/d/yyyy",
  disabled = false,
  placeholder = "Select date",
  id,
  className,
  align = "start",
}: DatePartsPickerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PickerView>("days");
  const [popoverSide, setPopoverSide] = useState<"top" | "bottom">("bottom");
  const [month, setMonth] = useState<Date>(() => parseIso(value) ?? new Date());
  const [yearPage, setYearPage] = useState(() => yearPageStart(new Date().getFullYear()));

  const min = minDate ?? new Date(fromYear, 0, 1);
  const max = maxDate ?? new Date(toYear, 11, 31);
  const minYear = min.getFullYear();
  const maxYear = max.getFullYear();

  const selected = useMemo(() => parseIso(value), [value]);

  const defaultMonth = useMemo(() => {
    if (selected) return startOfMonth(selected);
    const now = new Date();
    if (maxDate && now > maxDate) return startOfMonth(maxDate);
    const y = Math.min(Math.max(now.getFullYear(), fromYear), toYear);
    let m = now.getMonth();
    if (maxDate && y === maxDate.getFullYear() && m > maxDate.getMonth()) {
      m = maxDate.getMonth();
    }
    return new Date(y, m, 1);
  }, [selected, fromYear, toYear, maxDate]);

  const focusYear = month.getFullYear();

  useEffect(() => {
    if (!open) {
      setView("days");
      return;
    }
    const anchor = selected ?? defaultMonth;
    setMonth(anchor);
    setYearPage(yearPageStart(anchor.getFullYear()));
  }, [open, selected, defaultMonth]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayAllowed =
    isDateInRange(today, min, max, maxDate) && !(isDateDisabled?.(today) ?? false);

  const handleSelectDay = (date: Date | undefined) => {
    if (!date) return;
    if (!isDateInRange(date, min, max, maxDate)) return;
    if (isDateDisabled?.(date)) return;
    onChange(format(date, "yyyy-MM-dd"));
    setOpen(false);
  };

  const handleToday = () => {
    if (!todayAllowed) return;
    onChange(format(today, "yyyy-MM-dd"));
    setOpen(false);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (next && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      setPopoverSide(
        spaceBelow >= PICKER_ESTIMATED_HEIGHT_PX || spaceBelow >= spaceAbove ? "bottom" : "top",
      );
    }
    setOpen(next);
    if (!next) setView("days");
  };

  const handleSelectMonth = (monthIndex: number) => {
    setMonth(new Date(focusYear, monthIndex, 1));
    setView("days");
  };

  const handleSelectYear = (year: number) => {
    setMonth(new Date(year, month.getMonth(), 1));
    setView("months");
  };

  const canPrevYear = focusYear > minYear;
  const canNextYear = focusYear < maxYear;
  const canPrevYearPage = yearPage > yearPageStart(minYear);
  const canNextYearPage = yearPage + YEAR_PAGE_SIZE - 1 < maxYear;

  const DayCaption = useCallback(
    (props: CaptionProps) => (
      <DayViewCaption
        {...props}
        min={min}
        max={max}
        onOpenMonths={() => setView("months")}
        onOpenYears={() => {
          setYearPage(yearPageStart(props.displayMonth.getFullYear()));
          setView("years");
        }}
      />
    ),
    [min, max],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            "glass-input flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm",
            "ring-offset-background transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            open && "ring-2 ring-ring border-primary/60",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
        >
          <span className={cn("flex-1 truncate text-left", value ? "text-foreground" : "text-muted-foreground")}>
            {value ? displayValue(value, displayFormat) : placeholder}
          </span>
          <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[19.5rem] p-0 z-[100] rounded-lg border border-border bg-card text-card-foreground shadow-md"
        align={align}
        side={popoverSide}
        sideOffset={4}
        avoidCollisions
        collisionPadding={12}
      >
        <div className={cn(PICKER_BODY_HEIGHT_CLASS, "overflow-hidden")}>
          {view === "months" ? (
          <MonthPickerPanel
            year={focusYear}
            selected={selected}
            min={min}
            max={max}
            canPrevYear={canPrevYear}
            canNextYear={canNextYear}
            onPrevYear={() => setMonth((m) => new Date(m.getFullYear() - 1, m.getMonth(), 1))}
            onNextYear={() => setMonth((m) => new Date(m.getFullYear() + 1, m.getMonth(), 1))}
            onOpenYears={() => {
              setYearPage(yearPageStart(focusYear));
              setView("years");
            }}
            onSelectMonth={handleSelectMonth}
          />
        ) : null}

        {view === "years" ? (
          <YearPickerPanel
            pageStart={yearPage}
            focusYear={focusYear}
            selected={selected}
            minYear={minYear}
            maxYear={maxYear}
            canPrevPage={canPrevYearPage}
            canNextPage={canNextYearPage}
            onPrevPage={() => setYearPage((p) => Math.max(yearPageStart(minYear), p - YEAR_PAGE_SIZE))}
            onNextPage={() => setYearPage((p) => Math.min(yearPageStart(maxYear), p + YEAR_PAGE_SIZE))}
            onSelectYear={handleSelectYear}
          />
        ) : null}

        {view === "days" ? (
          <Calendar
            mode="single"
            weekStartsOn={1}
            fromDate={min}
            toDate={max}
            month={month}
            onMonthChange={setMonth}
            selected={selected}
            onSelect={handleSelectDay}
            disabled={(date) => !isDateInRange(date, min, max, maxDate) || (isDateDisabled?.(date) ?? false)}
            initialFocus
            fixedWeeks
            showOutsideDays
            components={{ Caption: DayCaption }}
            formatters={{
              formatWeekdayName: (date) => {
                const labels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;
                return labels[date.getDay()] ?? format(date, "EEE").slice(0, 2);
              },
            }}
            className="p-0"
            classNames={{
              months: "flex flex-col",
              month: "px-3 pb-3 pt-0 space-y-2",
              caption: "p-0",
              nav: "hidden",
              table: "w-full border-collapse",
              head_row: "grid grid-cols-7 mb-1",
              head_cell:
                "text-muted-foreground font-medium text-[0.7rem] uppercase text-center flex items-center justify-center",
              row: "grid grid-cols-7 mt-0.5",
              cell: "relative flex h-9 items-center justify-center p-0 text-center text-sm focus-within:relative focus-within:z-10",
              day: cn(
                "mx-auto h-9 w-9 p-0 font-normal rounded-full",
                "hover:bg-muted hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              ),
              day_selected:
                "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
              day_today: "text-destructive font-semibold aria-selected:text-primary-foreground",
              day_outside: "text-muted-foreground/35 aria-selected:text-primary-foreground",
              day_disabled: "text-muted-foreground/30 opacity-50",
            }}
          />
        ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 shrink-0">
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleClear} disabled={!value}>
            Clear
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleToday} disabled={!todayAllowed}>
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
